import { AloftPrice } from '@aloft/shared-objects';
import { formatRevisionType, noop, orUndefined, schAloftPrice } from '@aloft/toolkit-shared';
import { Block, CheckboxGroup, Flex, Link, Modal, RadioGroup, SimpleInput, Typography, colors } from '@aloft/toolkit-sui';

import { useEffect, useMemo, useState } from 'react';
import { Controller, Form, FormProvider, SubmitHandler } from 'react-hook-form';

import { FormTextarea } from '../../components/FormTextarea/FormTextarea.tsx';
import { useTkv2ReferenceBarEvents } from '../../components/ReferenceBar/useTkv2ReferenceBarEvents.ts';
import { useAppraisifyValidation } from '../../hooks/validation/useAppraisifyValidation.ts';
import { submitReconciliation } from '../../redux/features/reconciliationSlice.ts';
import {
  setReferenceViewOpen,
  setSelectedDataSheet,
} from '../../redux/features/referenceBarUISlice.ts';
import { getColorFromAdjustment } from '../../redux/features/selectors/adjustmentSelectors/utils/getColorFromAdjustment.ts';
import { selectCommentary } from '../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectOrder } from '../../redux/features/selectors/orderSelectors.ts';
import { selectEstimatedROI } from '../../redux/features/selectors/selectEstimatedROI.ts';
import { selectReconciliation } from '../../redux/features/selectors/selectReconciliation.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { ReconciliationSummarizedValues } from './components/ReconciliationSummarizedValues.tsx';
import { useReconciliationForm } from './hooks/useReconciliationForm.ts';
import { ReconciliationFormOutput } from './schemas.ts';

const VALUE_TYPE_ITEMS = [
  {
    label: 'As-is',
    value: 'as-is'
  },
  {
    label: 'Subject to the following repairs or alterations on the basis of a hypothetical condition that the repairs or alterations have been completed',
    value: 'subject-to'
  }
]

export const ReconciliationModule = () => {
  const dispatch = useAppDispatch();
  const form = useReconciliationForm();
  const { reconciliation: reconciliationCommentary } = useAppSelector(selectCommentary);
  const reconciliation = useAppSelector(selectReconciliation);
  const order = useAppSelector(selectOrder);
  const { isRevision, revisionData, hasArvOption: isArvOrder, isMock: isMockOrder } = order;
  const revisionCommentaryLabel = `${formatRevisionType(revisionData?.revisionType || 'rov')} Commentary`;
  const [showSubjectToModal, setShowSubjectToModal] = useState(false);

  // Only reset form on initial load, not on every state change
  useEffect(() => {
    form.reset({
      reconciliationCommentary: reconciliationCommentary?.reconciliationCommentary ?? '',
      highestAndBestUseCommentary: reconciliationCommentary?.highestAndBestUseCommentary ?? '',
      totalCostToCure: orUndefined(schAloftPrice.parse)(reconciliation.totalCostToCure),
      opinionOfMarketValue: orUndefined(schAloftPrice.parse)(reconciliation.opinionOfMarketValue),
      opinionOfAfterRepairValue: orUndefined(schAloftPrice.parse)(
        reconciliation.opinionOfAfterRepairValue,
      ),
      totalRenovationBudget: orUndefined(schAloftPrice.parse)(reconciliation.totalRenovationBudget),
      revisionCommentary: reconciliationCommentary?.revisionCommentary ?? '',
      alternativeComparablesCommentary:
        reconciliationCommentary?.alternativeComparablesCommentary ?? [],
      feasibilityCommentary: reconciliationCommentary?.feasibilityCommentary ?? '',
      valueTypes: reconciliation.valueTypes
    });
  }, [reconciliationCommentary]);

  const validation = useAppraisifyValidation();
  const {
    totalCostToCure: totalCostToCureError,
    opinionOfMarketValue: opinionOfMarketValueError,
    opinionOfAfterRepairValue: opinionOfAfterRepairValueError,
    totalRenovationBudget: totalRenovationBudgetError,
    highestAndBestUseCommentary: highestAndBestUseCommentaryError,
    reconciliationCommentary: reconciliationCommentaryError,
    revisionCommentary: revisionCommentaryError,
    alternativeComparablesCommentary: alternativeComparablesCommentaryError,
    feasibilityCommentary: feasibilityCommentaryError,
  } = validation.errors.Reconciliation;

  const estimatedROI = isArvOrder ? useAppSelector(selectEstimatedROI) : undefined;

  const shouldShowFeasibilityCommentary = useMemo(() => {
    if (!estimatedROI) return false;
    return estimatedROI.toDollarLike() <= 0;
  }, [estimatedROI]);

  useEffect(() => {
    dispatch(setSelectedDataSheet('pdf'));
    dispatch(setReferenceViewOpen(true));
  }, []);

  const onSubmit: SubmitHandler<ReconciliationFormOutput> = (data) => {
    dispatch(submitReconciliation({ ...data }));
  };

  const handleChange = () => {
    form.handleSubmit(onSubmit, undefined)().catch(noop);
  };

  let roiDisplay = 'TBD';
  if (estimatedROI) {
    roiDisplay =
      estimatedROI.toDollarLike() < 0
        ? `-$${Math.abs(estimatedROI.toDollarLike()).toLocaleString()}`
        : estimatedROI.toString(true);
  }

  return (
    <Flex {...useTkv2ReferenceBarEvents()} width="100%">
      <Modal
        isOpen={showSubjectToModal}
        onClose={() => setShowSubjectToModal(false)}
        title="Stop-and-Call Criteria"
        showCloseButton={false}
        primaryAction={{
          text: 'I understand',
          onClick: () => setShowSubjectToModal(false),
        }}
      >
        <Typography variant="body1" color="textSecondary">
          You have selected “Subject-To.” Per the Letter of Engagement (LOE) for this assignment, the client does not accept Subject-To appraisals or appraisals with hypothetical conditions. All orders are expected to be completed As-Is unless escalated and approved in advance.
        </Typography>
        <br></br>
        <Typography variant="h4" color="textPrimary">
          If you believe a Subject-To condition is necessary, do not sign the appraisal at this time. Proceed with these next steps:
        </Typography>
        <ul>
          <li style={{ listStyle: 'disc', marginLeft: '20px' }}>
            <Typography variant="body1" color="textSecondary">
              Email <Link isExternal href="mailto:orders@appraisify.com">orders@appraisify.com</Link> explaining why an As-Is appraisal cannot be completed and the specific condition(s) driving this determination.
            </Typography>
          </li>
          <li style={{ listStyle: 'disc', marginLeft: '20px' }}>
            <Typography variant="body1" color="textSecondary">
              We will escalate the issue to the client to determine whether the order should proceed, be modified, or be canceled.
            </Typography>
          </li>
          <li style={{ listStyle: 'disc', marginLeft: '20px' }}>
            <Typography variant="body1" color="textSecondary">
              Proceeding without approval may result in rejection of the report.
            </Typography>
          </li>
        </ul>
        <br></br>
        <Typography variant="body1" color="textSecondary">Please confirm that you understand these requirements before continuing.</Typography>
      </Modal>
      <FormProvider {...form}>
        <Form style={{ width: '100%' }} onChange={handleChange}>
          <Flex
            margin="15px"
            flexDirection="column"
            gap={4}
            flex={1}
            width="95%"
            minWidth="600px"
            maxWidth="900px"
            paddingBottom="50px"
          >
            <Typography variant="h1">Reconciliation</Typography>
            {isArvOrder ? (
              <>
                <Flex flex={1} justifyContent="space-between" alignItems="center">
                  <Block>
                    <Typography variant="h3" color="textSecondary">
                      Renovation Budget
                      <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                        {!isMockOrder && '*'}
                      </span>
                    </Typography>
                  </Block>
                  <Controller
                    name="totalRenovationBudget"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <SimpleInput
                        type="number"
                        value={field.value ? field.value.toString(true, true) : ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const value = e.target.value
                            ? AloftPrice.fromDollarLike(e.target.value)
                            : undefined;
                          field.onChange(value);
                          handleChange();
                        }}
                        onBlur={field.onBlur}
                        precision={0}
                        prefix="$ "
                        useSeparator
                        required={!isMockOrder}
                        placeholder={isMockOrder ? 'Optional' : 'Required'}
                        width="300px"
                        errorMessage={
                          fieldState.isTouched ? totalRenovationBudgetError?.message : undefined
                        }
                        invalid={fieldState.invalid && fieldState.isTouched}
                      />
                    )}
                  />
                </Flex>

                <Flex flex={1} justifyContent="space-between" alignItems="center">
                  <Block>
                    <Flex alignItems="center" gap={2}>
                      <Typography variant="h3" color="textSecondary">
                        Opinion of As-Is Value
                        <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                          {!isMockOrder && '*'}
                        </span>
                      </Typography>
                      <Typography
                        color={colors.aivText as any}
                        __dangerDoNotUseSx={{
                          backgroundColor: colors.aiv,
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                        }}
                      >
                        AIV
                      </Typography>
                    </Flex>
                  </Block>
                  <Controller
                    name="opinionOfMarketValue"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <SimpleInput
                        type="number"
                        value={field.value ? field.value.toString(true, true) : ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const trimmedValue = e.target.value.trim();
                          const value = trimmedValue
                            ? AloftPrice.fromDollarLike(trimmedValue)
                            : undefined;
                          field.onChange(value);
                          handleChange();
                        }}
                        onBlur={field.onBlur}
                        precision={0}
                        prefix="$ "
                        useSeparator
                        required={!isMockOrder}
                        placeholder={isMockOrder ? 'Optional' : 'Required'}
                        width="300px"
                        errorMessage={
                          fieldState.isTouched ? opinionOfMarketValueError?.message : undefined
                        }
                        invalid={fieldState.invalid && fieldState.isTouched}
                      />
                    )}
                  />
                </Flex>
                <ReconciliationSummarizedValues compType="aiv" />
                <Flex flex={1} justifyContent="space-between" alignItems="center">
                  <Block>
                    <Flex alignItems="center" gap={2}>
                      <Typography variant="h3" color="textSecondary">
                        Opinion of After-Repair Value
                        <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                          {!isMockOrder && '*'}
                        </span>
                      </Typography>
                      <Typography
                        color="textOffset"
                        __dangerDoNotUseSx={{
                          backgroundColor: colors.arv,
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                        }}
                      >
                        ARV
                      </Typography>
                    </Flex>
                  </Block>
                  <Controller
                    name="opinionOfAfterRepairValue"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <SimpleInput
                        type="number"
                        value={field.value ? field.value.toString(true, true) : ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const trimmedValue = e.target.value.trim();
                          const value = trimmedValue
                            ? AloftPrice.fromDollarLike(trimmedValue)
                            : undefined;
                          field.onChange(value);
                          handleChange();
                        }}
                        onBlur={field.onBlur}
                        precision={0}
                        prefix="$ "
                        useSeparator
                        required={!isMockOrder}
                        placeholder={isMockOrder ? 'Optional' : 'Required'}
                        width="300px"
                        errorMessage={
                          fieldState.isTouched ? opinionOfAfterRepairValueError?.message : undefined
                        }
                        invalid={fieldState.invalid && fieldState.isTouched}
                      />
                    )}
                  />
                </Flex>
                <ReconciliationSummarizedValues compType="arv" />
                <Flex flex={1} justifyContent="space-between" alignItems="center">
                  <Block>
                    <Typography variant="h3" color="textSecondary">
                      Estimated Return on Investment
                    </Typography>
                  </Block>
                  <Typography
                    variant="body1"
                    color={estimatedROI ? getColorFromAdjustment(estimatedROI) : 'textPrimary'}
                  >
                    {roiDisplay}
                  </Typography>
                </Flex>
                {shouldShowFeasibilityCommentary && (
                  <Flex flex={1} flexDirection="column">
                    <Controller
                      name="feasibilityCommentary"
                      control={form.control}
                      render={({ fieldState }) => (
                        <FormTextarea
                          name="feasibilityCommentary"
                          control={form.control}
                          label="Feasibility Commentary"
                          placeholder={
                            isMockOrder
                              ? 'Optional'
                              : 'Required. Provide justification as to why you believe the ROI on this property is negative.'
                          }
                          isRequired={!isMockOrder}
                          labelWidth="250px"
                          inputWidth="500px"
                          height="200px"
                          labelColor="textSecondary"
                          labelVariant="h3"
                          size="sm"
                          resize="vertical"
                          customErrorMessage={
                            fieldState.isTouched ? feasibilityCommentaryError?.message : undefined
                          }
                          column
                        />
                      )}
                    />
                  </Flex>
                )}
              </>
            ) : (
              <>
                {reconciliation?.includeTotalCostToCure && (
                  <Flex flex={1} justifyContent="space-between" alignItems="center">
                    <Block>
                      <Typography variant="h3" color="textSecondary">
                        Total Cost to Cure
                        <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                          {!isMockOrder && '*'}
                        </span>
                      </Typography>
                    </Block>
                    <Controller
                      name="totalCostToCure"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <SimpleInput
                          type="number"
                          value={field.value ? field.value.toString(true, true) : ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.value
                              ? AloftPrice.fromDollarLike(e.target.value)
                              : undefined;
                            field.onChange(value);
                            handleChange();
                          }}
                          onBlur={field.onBlur}
                          precision={0}
                          prefix="$ "
                          useSeparator
                          required={!isMockOrder}
                          placeholder={isMockOrder ? 'Optional' : 'Required'}
                          width="300px"
                          errorMessage={
                            fieldState.isTouched ? totalCostToCureError?.message : undefined
                          }
                          invalid={fieldState.invalid && fieldState.isTouched}
                        />
                      )}
                    />
                  </Flex>
                )}
                <Flex flex={1} justifyContent="space-between" alignItems="center">
                  <Block>
                    <Typography variant="h3" color="textSecondary">
                      Opinion of Market Value
                      <span className="simple-field-required" style={{ marginLeft: '2px' }}>
                        {!isMockOrder && '*'}
                      </span>
                    </Typography>
                  </Block>
                  <Controller
                    name="opinionOfMarketValue"
                    control={form.control}
                    render={({ field, fieldState }) => (
                      <SimpleInput
                        type="number"
                        value={field.value ? field.value.toString(true, true) : ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const value = e.target.value
                            ? AloftPrice.fromDollarLike(e.target.value)
                            : undefined;
                          field.onChange(value);
                          handleChange();
                        }}
                        onBlur={field.onBlur}
                        precision={0}
                        prefix="$ "
                        useSeparator
                        required={!isMockOrder}
                        placeholder={isMockOrder ? 'Optional' : 'Required'}
                        width="300px"
                        errorMessage={
                          fieldState.isTouched ? opinionOfMarketValueError?.message : undefined
                        }
                        invalid={fieldState.invalid && fieldState.isTouched}
                      />
                    )}
                  />
                </Flex>
                <ReconciliationSummarizedValues />
              </>
            )}
            {isRevision && (
              <>
                <Flex flex={1} flexDirection="column">
                  <Controller
                    name="revisionCommentary"
                    control={form.control}
                    render={({ fieldState }) => (
                      <FormTextarea
                        name="revisionCommentary"
                        control={form.control}
                        label={revisionCommentaryLabel}
                        placeholder={isMockOrder ? 'Optional' : 'Required'}
                        isRequired={!isMockOrder}
                        labelWidth="250px"
                        inputWidth="500px"
                        height="200px"
                        labelColor="textSecondary"
                        labelVariant="h3"
                        size="sm"
                        resize="vertical"
                        customErrorMessage={
                          fieldState.isTouched ? revisionCommentaryError?.message : undefined
                        }
                        column
                      />
                    )}
                  />
                </Flex>
                {revisionData?.comparables?.map((comp, index) => (
                  <Flex flex={1} flexDirection="column" key={comp.address}>
                    <Controller
                      name={`alternativeComparablesCommentary.${index}` as const}
                      control={form.control}
                      render={({ fieldState }) => (
                        <FormTextarea
                          name={`alternativeComparablesCommentary.${index}` as const}
                          control={form.control}
                          label={`Alternative Comparable ${index + 1}: ${comp.address}`}
                          placeholder={isMockOrder ? 'Optional' : 'Required'}
                          isRequired={!isMockOrder}
                          labelWidth="250px"
                          inputWidth="500px"
                          labelColor="textSecondary"
                          labelVariant="h3"
                          size="sm"
                          resize="vertical"
                          column
                          customErrorMessage={
                            fieldState.isTouched &&
                              reconciliationCommentary?.alternativeComparablesCommentary?.[index] ===
                              ''
                              ? alternativeComparablesCommentaryError?.message
                              : undefined
                          }
                        />
                      )}
                    />
                  </Flex>
                ))}
              </>
            )}
            <Flex flex={1} flexDirection="column">
              <Controller
                name="reconciliationCommentary"
                control={form.control}
                render={({ fieldState }) => (
                  <FormTextarea
                    name="reconciliationCommentary"
                    control={form.control}
                    label="Reconciliation Commentary"
                    placeholder={isMockOrder ? 'Optional' : 'Required'}
                    isRequired={!isMockOrder}
                    labelWidth="250px"
                    inputWidth="500px"
                    labelColor="textSecondary"
                    labelVariant="h3"
                    size="sm"
                    resize="vertical"
                    height="300px"
                    customErrorMessage={
                      fieldState.isTouched ? reconciliationCommentaryError?.message : undefined
                    }
                    column
                    subHeaderContent={
                      <Flex
                        backgroundColor="surface3"
                        borderColor="strokeDefault"
                        borderStyle="solid"
                        borderWidth="1px"
                        borderRadius="5px"
                        padding="15px"
                        flexDirection="column"
                        marginBottom="15px"
                        gap={4}
                      >
                        <Typography variant="body1" color="textSecondary">
                          This appraisal is made:
                        </Typography>
                        <Controller
                          name="valueTypes"
                          control={form.control}
                          render={({ field }) => (
                            <>
                              {isArvOrder ? (
                                <CheckboxGroup
                                  items={VALUE_TYPE_ITEMS}
                                  size="md"
                                  value={field.value}
                                  isDisabled
                                  gap="8px"
                                  textVariant="h5"
                                />
                              ) : (
                                <RadioGroup
                                  name="valueType"
                                  items={VALUE_TYPE_ITEMS}
                                  onChange={(value) => {
                                    if (value === 'subject-to' && !reconciliation.allowSubjectToValue) {
                                      setShowSubjectToModal(true);
                                    }
                                    field.onChange([value]);
                                    handleChange();
                                  }}
                                  value={field.value?.[0]}
                                  size="md"
                                  textVariant="h5"
                                  gap="8px"
                                />
                              )}
                            </>
                          )}
                        />
                      </Flex>
                    }
                  />
                )}
              />
            </Flex>
            <Flex flex={1} flexDirection="column">
              <Controller
                name="highestAndBestUseCommentary"
                control={form.control}
                render={({ fieldState }) => (
                  <FormTextarea
                    name="highestAndBestUseCommentary"
                    control={form.control}
                    label="Highest & Best Use Commentary"
                    placeholder={isMockOrder ? 'Optional' : 'Required'}
                    isRequired={!isMockOrder}
                    height="300px"
                    labelWidth="250px"
                    inputWidth="500px"
                    labelColor="textSecondary"
                    labelVariant="h3"
                    size="sm"
                    resize="vertical"
                    customErrorMessage={
                      fieldState.isTouched ? highestAndBestUseCommentaryError?.message : undefined
                    }
                    column
                  />
                )}
              />
            </Flex>
          </Flex>
        </Form>
      </FormProvider>
    </Flex>
  );
};
